import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import { useDataCache } from "../context/DataCacheContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { API, API_BASE_URL } from "../config/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { ScrollArea } from "../components/ui/scroll-area";
import { CaseSelect } from "../components/CaseSelect";
import { ClientSelect } from "../components/ClientSelect";
import {
  CheckSquare,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  CalendarIcon,
  MessageSquare,
  Clock,
  CheckCircle,
  AlertCircle,
  Send,
  Upload,
  FileText,
  Check,
  Search,
  ArrowUpDown,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { format } from "date-fns";
import { ro } from "date-fns/locale";


const initialFormState = {
  description: "",
  deadline: "",
  case_id: "",
  client_id: "",
  client_name: "",
  assigned_to: "",
  assigned_to_list: [],
};

export default function TasksPage() {
  const { user, getAuthHeader } = useAuth();
  const { fetchTasks: fetchCachedTasks, fetchCases: fetchCachedCases, fetchCasesBC: fetchCachedCasesBC, fetchTeamMembers: fetchCachedTeamMembers, fetchClients: fetchCachedClients, invalidateCache } = useDataCache();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tasks, setTasks] = useState([]);
  const [cases, setCases] = useState([]);
  const [casesBC, setCasesBC] = useState([]);
  const [clients, setClients] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [formData, setFormData] = useState(initialFormState);
  const [submitting, setSubmitting] = useState(false);
  const [deadline, setDeadline] = useState(null);
  const [noteContent, setNoteContent] = useState("");
  const [noteDocument, setNoteDocument] = useState(null);
  const [filter, setFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("default"); // "default", "deadline", "case"
  const [filterLawyer, setFilterLawyer] = useState("all"); // "all" or a specific user id
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [completeDocument, setCompleteDocument] = useState(null);
  const [taskDocuments, setTaskDocuments] = useState([]);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [highlightedTaskId, setHighlightedTaskId] = useState(null);
  const taskRefs = useRef({});

  const isAdmin = user?.role === "admin";

  const fetchData = useCallback(async (forceRefresh = false) => {
    try {
      const [tasksData, casesData, casesBCData, clientsData, teamData] = await Promise.all([
        fetchCachedTasks(forceRefresh),
        isAdmin ? fetchCachedCases(forceRefresh) : Promise.resolve([]),
        isAdmin ? fetchCachedCasesBC(forceRefresh) : Promise.resolve([]),
        isAdmin ? fetchCachedClients(forceRefresh) : Promise.resolve([]),
        isAdmin ? fetchCachedTeamMembers(forceRefresh) : Promise.resolve([]),
      ]);
      setTasks(tasksData);
      setCases(casesData);
      setCasesBC(casesBCData);
      setClients(clientsData);
      setTeamMembers(teamData);
    } catch (error) {
      toast.error("Eroare la încărcarea datelor");
    } finally {
      setLoading(false);
    }
  }, [fetchCachedTasks, fetchCachedCases, fetchCachedCasesBC, fetchCachedClients, fetchCachedTeamMembers, isAdmin]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle highlighting task from URL parameter
  useEffect(() => {
    const taskId = searchParams.get("task");
    if (taskId && tasks.length > 0) {
      setHighlightedTaskId(taskId);
      // Scroll to the task
      setTimeout(() => {
        if (taskRefs.current[taskId]) {
          taskRefs.current[taskId].scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 100);
      // Clear the URL parameter
      setSearchParams({});
      // Remove highlight after 3 seconds
      setTimeout(() => setHighlightedTaskId(null), 3000);
    }
  }, [searchParams, tasks, setSearchParams]);

  const handleOpenDialog = (task = null) => {
    if (task) {
      setSelectedTask(task);
      const clientName = task.client_name || task.title || "";
      setFormData({
        description: task.description || "",
        deadline: task.deadline,
        case_id: task.case_id || "",
        client_id: task.client_id || "",
        client_name: clientName,
        assigned_to: task.assigned_to,
      });
      setDeadline(task.deadline ? new Date(task.deadline) : null);
    } else {
      setSelectedTask(null);
      setFormData(initialFormState);
      setDeadline(null);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedTask(null);
    setFormData(initialFormState);
    setDeadline(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Verificare pentru crearea de sarcini noi (atribuire multiplă) sau editare
    if (selectedTask) {
      // Editare - folosește assigned_to simplu
      if (!formData.client_name || !formData.assigned_to || !deadline) {
        toast.error("Completați clientul, avocatul și termenul limită");
        return;
      }
    } else {
      // Creare nouă - verifică dacă avem cel puțin un avocat selectat
      if (!formData.client_name || formData.assigned_to_list.length === 0 || !deadline) {
        toast.error("Completați clientul, selectați cel puțin un avocat și termenul limită");
        return;
      }
    }

    setSubmitting(true);
    try {
      if (selectedTask) {
        // Editare sarcină existentă
        const submitData = {
          ...formData,
          deadline: format(deadline, "yyyy-MM-dd"),
          case_id: formData.case_id || null,
          client_id: formData.client_id || null,
          client_name: formData.client_name || null,
        };
        await axios.put(`${API}/tasks/${selectedTask.id}`, submitData, getAuthHeader());
        toast.success("Sarcină actualizată");
      } else {
        // Creare sarcini noi - pentru unul sau mai mulți avocați
        if (formData.assigned_to_list.length === 1) {
          // Un singur avocat - creare normală
          const submitData = {
            description: formData.description,
            deadline: format(deadline, "yyyy-MM-dd"),
            case_id: formData.case_id || null,
            client_id: formData.client_id || null,
            client_name: formData.client_name || null,
            assigned_to: formData.assigned_to_list[0],
          };
          await axios.post(`${API}/tasks`, submitData, getAuthHeader());
          toast.success("Sarcină creată și notificare trimisă");
        } else {
          // Mai mulți avocați - folosește bulk-create
          const submitData = {
            description: formData.description,
            deadline: format(deadline, "yyyy-MM-dd"),
            case_id: formData.case_id || null,
            client_id: formData.client_id || null,
            client_name: formData.client_name || null,
            assigned_to_list: formData.assigned_to_list,
          };
          await axios.post(`${API}/tasks/bulk-create`, submitData, getAuthHeader());
          toast.success(`${formData.assigned_to_list.length} sarcini create și notificări trimise`);
        }
      }
      handleCloseDialog();
      invalidateCache("tasks");
      fetchData(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operațiune eșuată");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedTask) return;

    try {
      await axios.delete(`${API}/tasks/${selectedTask.id}`, getAuthHeader());
      toast.success("Sarcină ștearsă");
      setDeleteDialogOpen(false);
      setSelectedTask(null);
      invalidateCache("tasks");
      fetchData(true);
    } catch (error) {
      toast.error("Eroare la ștergerea sarcinii");
    }
  };

  const handleToggleComplete = async (task) => {
    try {
      if (task.is_completed) {
        await axios.post(`${API}/tasks/${task.id}/uncomplete`, {}, getAuthHeader());
        toast.success("Sarcină marcată ca nefinalizată");
        invalidateCache("tasks");
        fetchData(true);
      } else {
        // Open dialog to optionally upload a completed document
        setSelectedTask(task);
        setCompleteDialogOpen(true);
      }
    } catch (error) {
      toast.error("Eroare la actualizarea sarcinii");
    }
  };

  const handleConfirmTask = async (taskId) => {
    try {
      await axios.post(`${API}/tasks/${taskId}/confirm`, {}, getAuthHeader());
      toast.success("Sarcină confirmată cu succes!");
      invalidateCache("tasks");
      fetchData(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la confirmarea sarcinii");
    }
  };

  const handleCompleteWithDocument = async () => {
    if (!selectedTask || submitting) return;
    
    try {
      setSubmitting(true);
      let documentData = {};
      
      if (completeDocument) {
        // First upload the document
        const formData = new FormData();
        formData.append("file", completeDocument);
        const uploadRes = await axios.post(
          `${API}/tasks/${selectedTask.id}/documents`,
          formData,
          { ...getAuthHeader(), headers: { ...getAuthHeader().headers, "Content-Type": "multipart/form-data" } }
        );
        documentData = {
          document_name: completeDocument.name,
          document_url: uploadRes.data.document.url
        };
      }
      
      await axios.post(
        `${API}/tasks/${selectedTask.id}/complete-with-document`,
        documentData,
        getAuthHeader()
      );
      
      toast.success("Sarcină finalizată! Administratorul a fost notificat.");
      setCompleteDialogOpen(false);
      setCompleteDocument(null);
      setSelectedTask(null);
      invalidateCache("tasks");
      fetchData(true);
    } catch (error) {
      toast.error("Eroare la finalizarea sarcinii");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddNote = async () => {
    if (!noteContent.trim() || !selectedTask) return;

    try {
      setSubmitting(true);
      let documentData = {};
      
      // Upload note document if present
      if (noteDocument) {
        const formData = new FormData();
        formData.append("file", noteDocument);
        const uploadRes = await axios.post(
          `${API}/tasks/${selectedTask.id}/documents`,
          formData,
          { ...getAuthHeader(), headers: { ...getAuthHeader().headers, "Content-Type": "multipart/form-data" } }
        );
        documentData = {
          document_name: noteDocument.name,
          document_url: uploadRes.data.document.url
        };
      }
      
      await axios.post(
        `${API}/tasks/${selectedTask.id}/notes`,
        { content: noteContent, ...documentData },
        getAuthHeader()
      );
      toast.success("Notă adăugată");
      setNoteContent("");
      setNoteDocument(null);
      const response = await axios.get(`${API}/tasks/${selectedTask.id}`, getAuthHeader());
      setSelectedTask(response.data);
      invalidateCache("tasks");
      fetchData(true);
    } catch (error) {
      toast.error("Eroare la adăugarea notei");
    } finally {
      setSubmitting(false);
    }
  };

  const openViewDialog = (task) => {
    setSelectedTask(task);
    setViewDialogOpen(true);
  };

  const openDeleteDialog = (task) => {
    setSelectedTask(task);
    setDeleteDialogOpen(true);
  };

  const openNoteDialog = (task) => {
    setSelectedTask(task);
    setNoteDialogOpen(true);
  };

  const isOverdue = (deadlineStr, isCompleted) => {
    return new Date(deadlineStr) < new Date() && !isCompleted;
  };

  const getDaysInfo = (deadlineStr, isCompleted) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const deadline = new Date(deadlineStr);
    deadline.setHours(0, 0, 0, 0);
    const diffTime = deadline - today;
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
    if (isCompleted) return null;
    if (diffDays < 0) return { type: "overdue", days: Math.abs(diffDays) };
    if (diffDays === 0) return { type: "today", days: 0 };
    if (diffDays <= 3) return { type: "soon", days: diffDays };
    return { type: "ok", days: diffDays };
  };

  // Normalize for diacritic-insensitive search
  const normalizeSearch = (str) => (str || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

  // Stats
  const stats = useMemo(() => {
    return {
      total: tasks.length,
      pending: tasks.filter(t => !t.is_completed).length,
      overdue: tasks.filter(t => !t.is_completed && isOverdue(t.deadline, false)).length,
      awaitingConfirm: tasks.filter(t => t.is_completed && !t.admin_confirmed).length,
      confirmed: tasks.filter(t => t.is_completed && t.admin_confirmed).length,
    };
  }, [tasks]);

  const filteredTasks = useMemo(() => {
    let result = [...tasks];
    
    // Filter by status
    if (filter === "pending") result = result.filter(t => !t.is_completed);
    if (filter === "completed") result = result.filter(t => t.is_completed);
    
    // Filter by lawyer
    if (filterLawyer !== "all") {
      result = result.filter(t => t.assigned_to === filterLawyer);
    }
    
    // Search
    if (searchQuery.trim()) {
      const q = normalizeSearch(searchQuery);
      result = result.filter(t =>
        normalizeSearch(t.client_name).includes(q) ||
        normalizeSearch(t.title).includes(q) ||
        normalizeSearch(t.description).includes(q) ||
        normalizeSearch(t.assigned_to_name).includes(q) ||
        normalizeSearch(t.case_no).includes(q)
      );
    }
    
    // Sort
    if (sortBy === "deadline") {
      result.sort((a, b) => new Date(a.deadline) - new Date(b.deadline));
    } else if (sortBy === "case") {
      result.sort((a, b) => (a.case_no || "zzz").localeCompare(b.case_no || "zzz", 'ro'));
    }
    // default = no explicit sort (server order)
    
    return result;
  }, [tasks, filter, filterLawyer, searchQuery, sortBy]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse font-body text-muted-foreground">Se încarcă sarcinile...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="tasks-page">
      <div className="sticky top-0 z-10 bg-background pb-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">
            {isAdmin ? "Sarcini" : "Sarcinile Mele"}
          </h1>
          <p className="font-body text-muted-foreground mt-1">
            {isAdmin ? "Gestionează și atribuie sarcini" : "Sarcinile tale atribuite"}
          </p>
        </div>
        {isAdmin && (
          <Button onClick={() => handleOpenDialog()} data-testid="add-task-btn">
            <Plus className="w-4 h-4 mr-2" />
            Adaugă Sarcină
          </Button>
        )}
      </div>

      {/* 1. Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="tasks-stats">
        <div className={`rounded-xl border p-4 cursor-pointer transition-colors ${filter === 'all' ? 'ring-2 ring-primary' : 'hover:bg-secondary/50'}`} onClick={() => setFilter('all')}>
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Total</p>
          <p className="text-2xl font-bold mt-1">{stats.total}</p>
        </div>
        <div className={`rounded-xl border border-orange-200 bg-orange-50 p-4 cursor-pointer transition-colors ${filter === 'pending' ? 'ring-2 ring-orange-500' : ''}`} onClick={() => setFilter('pending')}>
          <p className="text-xs font-medium text-orange-600 uppercase tracking-wide">În Așteptare</p>
          <p className="text-2xl font-bold text-orange-800 mt-1">{stats.pending}</p>
        </div>
        <div className="rounded-xl border border-red-200 bg-red-50 p-4">
          <p className="text-xs font-medium text-red-600 uppercase tracking-wide">Întârziate</p>
          <p className="text-2xl font-bold text-red-800 mt-1">{stats.overdue}</p>
        </div>
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
          <p className="text-xs font-medium text-amber-600 uppercase tracking-wide">Așteaptă Confirmare</p>
          <p className="text-2xl font-bold text-amber-800 mt-1">{stats.awaitingConfirm}</p>
        </div>
        <div className={`rounded-xl border border-green-200 bg-green-50 p-4 cursor-pointer transition-colors ${filter === 'completed' ? 'ring-2 ring-green-500' : ''}`} onClick={() => setFilter('completed')}>
          <p className="text-xs font-medium text-green-600 uppercase tracking-wide">Confirmate</p>
          <p className="text-2xl font-bold text-green-800 mt-1">{stats.confirmed}</p>
        </div>
      </div>

      {/* 2+4+5. Toolbar: Search + Sort + Filter by Lawyer */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Caută după client, dosar, avocat..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="tasks-search-input"
          />
        </div>
        
        {/* Filter by status */}
        {[
          { key: "all", label: "Toate" },
          { key: "pending", label: "În Așteptare" },
          { key: "completed", label: "Finalizate" },
        ].map(f => (
          <Button
            key={f.key}
            variant={filter === f.key ? "default" : "outline"}
            size="sm"
            onClick={() => setFilter(f.key)}
            data-testid={`filter-tasks-${f.key}-btn`}
          >
            {f.label}
          </Button>
        ))}

        <div className="h-6 w-px bg-border" />

        {/* Sort buttons */}
        <Button
          variant={sortBy === "default" ? "default" : "outline"}
          size="sm"
          onClick={() => setSortBy("default")}
          data-testid="sort-default-btn"
        >
          <ArrowUpDown className="w-3.5 h-3.5 mr-1.5" />
          Implicit
        </Button>
        <Button
          variant={sortBy === "deadline" ? "default" : "outline"}
          size="sm"
          onClick={() => setSortBy("deadline")}
          data-testid="sort-deadline-btn"
        >
          <ArrowUpDown className="w-3.5 h-3.5 mr-1.5" />
          Termen
        </Button>
        <Button
          variant={sortBy === "case" ? "default" : "outline"}
          size="sm"
          onClick={() => setSortBy("case")}
          data-testid="sort-case-btn"
        >
          <ArrowUpDown className="w-3.5 h-3.5 mr-1.5" />
          Dosar
        </Button>

        {/* 4. Filter by lawyer (admin only) */}
        {isAdmin && teamMembers.length > 0 && (
          <>
            <div className="h-6 w-px bg-border" />
            <Select value={filterLawyer} onValueChange={setFilterLawyer}>
              <SelectTrigger className="w-44" data-testid="filter-lawyer-select">
                <Users className="w-3.5 h-3.5 mr-1.5" />
                <SelectValue placeholder="Toți avocații" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toți avocații</SelectItem>
                {teamMembers.map((m) => (
                  <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </>
        )}
      </div>
      </div>{/* End Sticky */}

      {/* Tasks List */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-xl flex items-center gap-2">
            <CheckSquare className="w-5 h-5" />
            {filter === "all"
              ? `Toate Sarcinile (${filteredTasks.length})`
              : filter === "pending"
              ? `Sarcini în Așteptare (${filteredTasks.length})`
              : `Sarcini Finalizate (${filteredTasks.length})`}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredTasks.length === 0 ? (
            <div className="text-center py-8">
              <CheckSquare className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              <p className="font-body text-muted-foreground">
                {filter === "pending"
                  ? "Nu ai sarcini în așteptare. Bravo!"
                  : filter === "completed"
                  ? "Nu ai sarcini finalizate încă."
                  : "Nu există sarcini încă."}
              </p>
              {isAdmin && filter === "all" && (
                <Button variant="outline" className="mt-4" onClick={() => handleOpenDialog()}>
                  Creează Prima Sarcină
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-1.5">
              {filteredTasks.map((task) => {
                const isConfirmedByAdmin = task.is_completed && task.admin_confirmed;
                const isCompletedButNotConfirmed = task.is_completed && !task.admin_confirmed;
                const isNotCompleted = !task.is_completed;
                const overdue = isOverdue(task.deadline, task.is_completed) && isNotCompleted;
                
                // Status config: border color + badge
                let borderClass = "border-l-[3px] ";
                let statusLabel = "";
                let statusBadgeClass = "";
                if (isConfirmedByAdmin) {
                  borderClass += "border-l-green-500 bg-green-50/30 opacity-60";
                  statusLabel = "Confirmat";
                  statusBadgeClass = "bg-green-100 text-green-700";
                } else if (isCompletedButNotConfirmed) {
                  borderClass += "border-l-amber-500 bg-amber-50/30";
                  statusLabel = "Așteaptă confirmare";
                  statusBadgeClass = "bg-amber-100 text-amber-700";
                } else if (overdue) {
                  borderClass += "border-l-red-600 bg-red-50/40";
                  const dInfo = getDaysInfo(task.deadline, task.is_completed);
                  statusLabel = `Întârziat ${dInfo?.days || ""}z`;
                  statusBadgeClass = "bg-red-100 text-red-700";
                } else if (isNotCompleted) {
                  const dInfo = getDaysInfo(task.deadline, task.is_completed);
                  if (dInfo?.type === "today") {
                    borderClass += "border-l-orange-500 bg-orange-50/30";
                    statusLabel = "Azi";
                    statusBadgeClass = "bg-orange-100 text-orange-700";
                  } else if (dInfo?.type === "soon") {
                    borderClass += "border-l-yellow-500 bg-yellow-50/30";
                    statusLabel = `${dInfo.days}z rămase`;
                    statusBadgeClass = "bg-yellow-100 text-yellow-700";
                  } else {
                    borderClass += "border-l-blue-500";
                    statusLabel = "În lucru";
                    statusBadgeClass = "bg-blue-100 text-blue-700";
                  }
                }
                
                return (
                <div
                  key={task.id}
                  ref={(el) => (taskRefs.current[task.id] = el)}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg border transition-all cursor-pointer hover:bg-secondary/30 ${borderClass} ${
                    highlightedTaskId === task.id ? "ring-2 ring-primary ring-offset-2 bg-primary/5" : ""
                  }`}
                  data-testid={`task-item-${task.id}`}
                  onClick={() => openNoteDialog(task)}
                >
                  <Checkbox
                    checked={task.is_completed}
                    onCheckedChange={() => handleToggleComplete(task)}
                    className="shrink-0"
                    data-testid={`task-checkbox-${task.id}`}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <div className="flex items-center gap-2 flex-1 min-w-0 flex-wrap">
                    {/* Client */}
                    <span className={`font-body text-sm font-medium truncate ${
                      isConfirmedByAdmin ? "line-through text-muted-foreground" : ""
                    }`}>
                      {task.client_name || task.title || "—"}
                    </span>
                    {/* Dosar */}
                    {task.case_no && (
                      <>
                        <span className="text-muted-foreground shrink-0">|</span>
                        <Badge 
                          variant="outline" 
                          className="font-mono text-[10px] px-1 py-0 cursor-pointer hover:bg-primary hover:text-primary-foreground shrink-0"
                          onClick={(e) => { e.stopPropagation(); if (task.case_id) navigate(`/cases?open=${task.case_id}`); }}
                          data-testid={`task-case-link-${task.id}`}
                        >
                          {task.case_no}
                        </Badge>
                      </>
                    )}
                    {/* Termen */}
                    <span className="text-muted-foreground shrink-0">|</span>
                    <span className="font-mono text-[10px] text-muted-foreground shrink-0">
                      {format(new Date(task.deadline), "dd/MM/yyyy")}
                    </span>
                    {/* Avocat */}
                    {isAdmin && task.assigned_to_name && (
                      <>
                        <span className="text-muted-foreground shrink-0">|</span>
                        <Badge variant="outline" className="text-[10px] px-1 py-0 shrink-0">{task.assigned_to_name}</Badge>
                      </>
                    )}
                    {/* Stadiu */}
                    <span className="text-muted-foreground shrink-0">|</span>
                    <Badge className={`text-[10px] px-1.5 py-0 shrink-0 ${statusBadgeClass}`}>{statusLabel}</Badge>
                    {/* Note count */}
                    {task.notes?.length > 0 && (
                      <span className="text-[10px] text-muted-foreground flex items-center gap-0.5 shrink-0">
                        <MessageSquare className="w-3 h-3" />{task.notes.length}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-0.5 shrink-0" onClick={(e) => e.stopPropagation()}>
                    {isAdmin && isCompletedButNotConfirmed && (
                      <Button size="sm" variant="outline" className="h-6 px-1.5 text-[10px] bg-green-50 hover:bg-green-100 text-green-700 border-green-300" onClick={() => handleConfirmTask(task.id)} data-testid={`confirm-task-btn-${task.id}`}>
                        <Check className="w-3 h-3" />
                      </Button>
                    )}
                    {isAdmin && (
                      <>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleOpenDialog(task)} data-testid={`edit-task-${task.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => openDeleteDialog(task)} data-testid={`delete-task-${task.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                      </>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog (Admin Only) */}
      {isAdmin && (
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-md" data-testid="task-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading">
                {selectedTask ? "Editează Sarcină" : "Creează Sarcină"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className="font-body">Client *</Label>
                <ClientSelect
                  clients={clients}
                  value={formData.client_id || "none"}
                  onValueChange={(value, customName) => {
                    if (value === "__custom__") {
                      setFormData({
                        ...formData,
                        client_id: "",
                        client_name: customName || "",
                      });
                    } else {
                      const selected = clients.find(c => c.id === value);
                      setFormData({
                        ...formData,
                        client_id: value === "none" ? "" : value,
                        client_name: selected ? (selected.nume || selected.denumire || "") : "",
                      });
                    }
                  }}
                  placeholder="Selectează sau scrie nume client"
                  emptyText="Niciunul"
                  allowNone={false}
                  allowCustom={true}
                  customDisplayName={formData.client_name || ""}
                  data-testid="select-client"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description" className="font-body">
                  Descriere (Opțional)
                </Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Detalii sarcină..."
                  rows={3}
                  data-testid="input-task-description"
                />
              </div>

              <div className="space-y-2">
                <Label className="font-body">Dosar Asociat (Opțional)</Label>
                <CaseSelect
                  cases={cases}
                  casesBC={casesBC}
                  value={formData.case_id || "none"}
                  onValueChange={(value) => setFormData({ ...formData, case_id: value })}
                  placeholder="Selectează dosar"
                  emptyText="Niciunul"
                  allowNone={true}
                  data-testid="select-case"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="font-body">Termen Limită *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal"
                        data-testid="deadline-picker"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {deadline ? format(deadline, "dd/MM/yyyy", { locale: ro }) : "Selectează data"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar mode="single" selected={deadline} onSelect={setDeadline} locale={ro} initialFocus />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label className="font-body">Atribuie Către *</Label>
                  {selectedTask ? (
                    // Editare - selectează un singur avocat
                    <Select
                      value={formData.assigned_to}
                      onValueChange={(value) => setFormData({ ...formData, assigned_to: value })}
                    >
                      <SelectTrigger data-testid="select-assigned-to">
                        <SelectValue placeholder="Selectează avocat" />
                      </SelectTrigger>
                      <SelectContent>
                        {teamMembers.map((member) => (
                          <SelectItem key={member.id} value={member.id}>
                            {member.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    // Creare nouă - checkbox-uri multiple
                    <div className="border rounded-md p-3 space-y-2 max-h-32 overflow-y-auto">
                      {teamMembers.length === 0 ? (
                        <p className="text-sm text-muted-foreground">Nu există membrii în echipă</p>
                      ) : (
                        teamMembers.map((member) => (
                          <div key={member.id} className="flex items-center space-x-2">
                            <Checkbox
                              id={`assign-${member.id}`}
                              checked={formData.assigned_to_list.includes(member.id)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setFormData({
                                    ...formData,
                                    assigned_to_list: [...formData.assigned_to_list, member.id]
                                  });
                                } else {
                                  setFormData({
                                    ...formData,
                                    assigned_to_list: formData.assigned_to_list.filter(id => id !== member.id)
                                  });
                                }
                              }}
                              data-testid={`checkbox-assign-${member.id}`}
                            />
                            <Label
                              htmlFor={`assign-${member.id}`}
                              className="text-sm font-normal cursor-pointer"
                            >
                              {member.name}
                            </Label>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                  {!selectedTask && formData.assigned_to_list.length > 1 && (
                    <p className="text-xs text-muted-foreground">
                      {formData.assigned_to_list.length} avocați selectați - se vor crea {formData.assigned_to_list.length} sarcini
                    </p>
                  )}
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={handleCloseDialog}>
                  Anulează
                </Button>
                <Button type="submit" disabled={submitting} data-testid="submit-task">
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Se salvează...
                    </>
                  ) : selectedTask ? (
                    "Actualizează"
                  ) : (
                    "Creează"
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {/* Notes Dialog */}
      <Dialog open={noteDialogOpen} onOpenChange={setNoteDialogOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="notes-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Detalii Sarcină</DialogTitle>
          </DialogHeader>
          {selectedTask && (
            <div className="space-y-4">
              {/* Task Info Card */}
              <div className="p-4 bg-muted rounded-lg space-y-3">
                {/* Client + Status row */}
                <div className="flex items-center justify-between gap-2">
                  <span className="font-body font-semibold text-base">
                    {selectedTask.client_name || selectedTask.title || "—"}
                  </span>
                  <Badge className={`text-xs px-2 py-0.5 shrink-0 ${
                    selectedTask.is_completed && selectedTask.admin_confirmed
                      ? "bg-green-100 text-green-700"
                      : selectedTask.is_completed
                      ? "bg-amber-100 text-amber-700"
                      : isOverdue(selectedTask.deadline, false)
                      ? "bg-red-100 text-red-700"
                      : "bg-blue-100 text-blue-700"
                  }`}>
                    {selectedTask.is_completed && selectedTask.admin_confirmed
                      ? "Confirmat"
                      : selectedTask.is_completed
                      ? "Așteaptă confirmare"
                      : isOverdue(selectedTask.deadline, false)
                      ? "Întârziat"
                      : "În lucru"}
                  </Badge>
                </div>

                {/* Info grid */}
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  {/* Dosar */}
                  <div>
                    <span className="text-muted-foreground text-xs block">Dosar</span>
                    {selectedTask.case_no ? (
                      <Badge
                        variant="outline"
                        className="font-mono text-xs cursor-pointer hover:bg-primary hover:text-primary-foreground"
                        onClick={() => { setNoteDialogOpen(false); navigate(`/cases?open=${selectedTask.case_id}`); }}
                      >
                        {selectedTask.case_no}
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </div>

                  {/* Termen */}
                  <div>
                    <span className="text-muted-foreground text-xs block">Termen Limită</span>
                    <span className={`font-mono text-sm ${
                      !selectedTask.is_completed && isOverdue(selectedTask.deadline, false)
                        ? "text-red-600 font-semibold"
                        : ""
                    }`}>
                      {selectedTask.deadline ? format(new Date(selectedTask.deadline), "dd.MM.yyyy") : "—"}
                    </span>
                  </div>

                  {/* Avocat */}
                  <div>
                    <span className="text-muted-foreground text-xs block">Avocat Repartizat</span>
                    <span className="font-body text-sm">{selectedTask.assigned_to_name || "—"}</span>
                  </div>

                  {/* Prioritate */}
                  <div>
                    <span className="text-muted-foreground text-xs block">Prioritate</span>
                    <Badge variant="outline" className={`text-xs ${
                      selectedTask.priority === "high" ? "border-red-300 text-red-600" :
                      selectedTask.priority === "low" ? "border-gray-300 text-gray-500" :
                      "border-blue-300 text-blue-600"
                    }`}>
                      {selectedTask.priority === "high" ? "Ridicată" :
                       selectedTask.priority === "low" ? "Scăzută" : "Normală"}
                    </Badge>
                  </div>
                </div>

                {/* Descriere */}
                {selectedTask.description && (
                  <div className="pt-2 border-t border-border/50">
                    <span className="text-muted-foreground text-xs block mb-1">Descriere</span>
                    <p className="font-body text-sm whitespace-pre-wrap">{selectedTask.description}</p>
                  </div>
                )}

                {/* Completat de / Confirmat de */}
                {selectedTask.is_completed && (
                  <div className="pt-2 border-t border-border/50 text-xs text-muted-foreground">
                    {selectedTask.completed_at && (
                      <span>Finalizat la: {format(new Date(selectedTask.completed_at), "dd.MM.yyyy HH:mm")}</span>
                    )}
                    {selectedTask.admin_confirmed && selectedTask.admin_confirmed_at && (
                      <span className="ml-3">| Confirmat la: {format(new Date(selectedTask.admin_confirmed_at), "dd.MM.yyyy HH:mm")}</span>
                    )}
                  </div>
                )}

                {/* Completed document */}
                {selectedTask.completed_document?.url && (
                  <div className="pt-2 border-t border-border/50">
                    <a
                      href={`${API_BASE_URL}${selectedTask.completed_document.url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-sm text-primary hover:underline"
                    >
                      <FileText className="w-4 h-4" />
                      {selectedTask.completed_document.name || "Document finalizare"}
                    </a>
                  </div>
                )}
              </div>

              {/* Notes List */}
              <div>
                <Label className="font-body text-muted-foreground mb-2 block">
                  Note ({selectedTask.notes?.length || 0})
                </Label>
                {selectedTask.notes?.length > 0 ? (
                  <ScrollArea className="h-56 rounded-md border p-3">
                    <div className="space-y-3">
                      {selectedTask.notes.map((note) => (
                        <div
                          key={note.id}
                          className="p-3 bg-secondary/50 rounded-md"
                          data-testid={`note-${note.id}`}
                        >
                          {/* Author, Date, Time header */}
                          <div className="flex items-center gap-2 mb-2 pb-2 border-b border-border/50">
                            <span className="font-body text-sm font-medium text-primary">
                              {note.created_by_name}
                            </span>
                            <span className="font-mono text-xs text-muted-foreground">
                              {note.date || format(new Date(note.created_at), "dd/MM/yyyy")}
                            </span>
                            <span className="font-mono text-xs text-muted-foreground">
                              {note.time || new Date(note.created_at).toLocaleTimeString("ro-RO", { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                          {/* Note content */}
                          <p className="font-body text-sm">{note.content}</p>
                          {/* Document attachment */}
                          {note.document?.url && (
                            <div className="mt-2 pt-2 border-t border-border/50">
                              <a 
                                href={`${API_BASE_URL}${note.document.url}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 text-sm text-primary hover:underline"
                              >
                                <FileText className="w-4 h-4" />
                                {note.document.name || "Document atașat"}
                              </a>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                ) : (
                  <p className="text-center py-4 text-sm text-muted-foreground border rounded-md">
                    Nu există note încă.
                  </p>
                )}
              </div>

              {/* Add Note */}
              <div className="space-y-2">
                <Textarea
                  value={noteContent}
                  onChange={(e) => setNoteContent(e.target.value)}
                  placeholder="Adaugă o notă..."
                  rows={3}
                  data-testid="note-input"
                />
                <div className="flex items-center justify-between gap-2">
                  <div className="flex-1">
                    <Label htmlFor="note-document" className="cursor-pointer">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
                        <Upload className="w-4 h-4" />
                        {noteDocument ? noteDocument.name : "Atașează document (opțional)"}
                      </div>
                    </Label>
                    <input
                      id="note-document"
                      type="file"
                      className="hidden"
                      onChange={(e) => setNoteDocument(e.target.files?.[0] || null)}
                    />
                  </div>
                  <Button
                    onClick={handleAddNote}
                    disabled={!noteContent.trim() || submitting}
                    data-testid="add-note-btn"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Complete Task with Document Dialog */}
      <Dialog open={completeDialogOpen} onOpenChange={setCompleteDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="complete-task-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Finalizează Sarcina</DialogTitle>
          </DialogHeader>
          {selectedTask && (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-muted rounded-md">
                <p className="font-body font-medium">{selectedTask.client_name || selectedTask.title}</p>
              </div>
              
              <div className="space-y-2">
                <Label className="font-body">Document finalizat (opțional)</Label>
                <p className="text-sm text-muted-foreground">
                  Poți încărca un document cu rezultatul sarcinii finalizate.
                </p>
                <div className="border-2 border-dashed rounded-md p-4">
                  <Label htmlFor="complete-document" className="cursor-pointer">
                    <div className="flex flex-col items-center gap-2 text-center">
                      <Upload className="w-8 h-8 text-muted-foreground" />
                      {completeDocument ? (
                        <span className="text-sm font-medium">{completeDocument.name}</span>
                      ) : (
                        <span className="text-sm text-muted-foreground">
                          Click pentru a selecta un document
                        </span>
                      )}
                    </div>
                  </Label>
                  <input
                    id="complete-document"
                    type="file"
                    className="hidden"
                    onChange={(e) => setCompleteDocument(e.target.files?.[0] || null)}
                  />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompleteDialogOpen(false)}>
              Anulează
            </Button>
            <Button onClick={handleCompleteWithDocument} disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Finalizează
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation (Admin Only) */}
      {isAdmin && (
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent data-testid="delete-task-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-heading">Șterge Sarcină</AlertDialogTitle>
              <AlertDialogDescription className="font-body">
                Ești sigur că vrei să ștergi sarcina "{selectedTask?.client_name || selectedTask?.title}"? Această acțiune nu poate fi anulată.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Anulează</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                data-testid="confirm-delete-task"
              >
                Șterge
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}
